import { useEffect, useState, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Upload, Trash2, Pencil, Loader2, Search, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 50;
const fmtPrice = (v) =>
  v == null || v === ""
    ? "—"
    : `₺${Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", unit: "adet", price: "" });
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const fileRef = useRef();

  const load = async (targetPage = 0, query = "") => {
    setLoading(true);
    try {
      const [list, cnt] = await Promise.all([
        api.get("/products", { params: { q: query || undefined, skip: targetPage * PAGE_SIZE, limit: PAGE_SIZE } }),
        api.get("/products/count", { params: { q: query || undefined } }),
      ]);
      setProducts(list.data);
      setTotal(cnt.data.count);
      setPage(targetPage);
      setSelected(new Set());
    } catch (e) {
      toast.error("Ürünler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  // Debounced search (also runs on mount with q="")
  useEffect(() => {
    const t = setTimeout(() => load(0, q), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const startCreate = () => { setEditing(null); setForm({ code: "", name: "", unit: "adet", price: "" }); setOpen(true); };
  const startEdit = (p) => { setEditing(p); setForm({ code: p.code, name: p.name, unit: p.unit, price: p.price ?? "" }); setOpen(true); };

  const save = async () => {
    try {
      const payload = {
        code: form.code,
        name: form.name,
        unit: form.unit,
        price: form.price === "" || form.price == null ? null : Number(form.price),
      };
      if (editing) {
        await api.patch(`/products/${editing.id}`, payload);
        toast.success("Güncellendi");
      } else {
        await api.post("/products", payload);
        toast.success("Eklendi");
      }
      setOpen(false);
      load(page, q);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/products/${id}`);
    load(page, q);
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allSelected = products.length > 0 && selected.size === products.length;
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(products.map((p) => p.id)));
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} ürünü silmek istediğinize emin misiniz?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/products/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${data.deleted} ürün silindi`);
      load(0, q);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleImport = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setImporting(true);
    try {
      const { data } = await api.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} yeni ürün eklendi${data.skipped ? `, ${data.skipped} mevcut (atlandı)` : ""}`);
      if (data.errors?.length) toast.warning(`${data.errors.length} hatalı satır atlandı`);
      load(0, "");
      setQ("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "İçe aktarma başarısız");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Ürünler</h1>
          <p className="text-sm text-slate-500 mt-1" data-testid="products-total">{total.toLocaleString("tr-TR")} ürün kayıtlı</p>
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleImport} data-testid="excel-input" />
          <Button data-testid="excel-import-btn" variant="outline" className="h-10 gap-2" disabled={importing} onClick={() => fileRef.current?.click()}>
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Excel İçe Aktar
          </Button>
          <Button data-testid="add-product-btn" className="h-10 gap-2 bg-slate-900 hover:bg-slate-800" onClick={startCreate}>
            <Plus className="h-4 w-4" /> Ürün Ekle
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          data-testid="product-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Kod veya açıklama ile ara..."
          className="h-11 pl-9"
        />
      </div>

      {/* Bulk action bar */}
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-t-lg">
        <label className="flex items-center gap-2.5 cursor-pointer select-none">
          <Checkbox data-testid="select-all-checkbox" checked={allSelected} onCheckedChange={toggleAll} />
          <span className="text-sm text-slate-600">
            {selected.size > 0 ? `${selected.size} seçili (bu sayfa)` : "Sayfayı seç"}
          </span>
        </label>
        {selected.size > 0 && (
          <Button
            data-testid="bulk-delete-btn"
            variant="destructive"
            size="sm"
            className="h-9 gap-2"
            disabled={bulkBusy}
            onClick={bulkDelete}
          >
            {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Seçilenleri Sil ({selected.size})
          </Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 border-t-0 divide-y divide-slate-100 min-h-[120px]">
        {loading ? (
          <div className="p-8 flex justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : products.length === 0 ? (
          <div data-testid="empty-products" className="p-8 text-center text-slate-400 text-sm">
            {q ? "Eşleşen ürün yok." : "Ürün yok. Excel ile içe aktarın veya elle ekleyin."}
          </div>
        ) : products.map((p) => (
          <div
            key={p.id}
            data-testid={`product-row-${p.code}`}
            className={`p-4 flex items-center gap-3 ${selected.has(p.id) ? "bg-blue-50/50" : ""}`}
          >
            <Checkbox
              data-testid={`select-product-${p.code}`}
              checked={selected.has(p.id)}
              onCheckedChange={() => toggleSelect(p.id)}
            />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-slate-900">{p.code} <span className="text-slate-400 text-sm">· {p.unit}</span></div>
              <div className="text-sm text-slate-600 truncate">{p.name}</div>
            </div>
            <div data-testid={`product-price-${p.code}`} className="text-sm font-semibold text-slate-900 tabular-nums shrink-0 w-24 text-right">
              {fmtPrice(p.price)}
            </div>
            <div className="flex gap-1 shrink-0">
              <button data-testid={`edit-product-${p.code}`} className="p-2 rounded-md hover:bg-slate-100 text-slate-500" onClick={() => startEdit(p)}><Pencil className="h-4 w-4" /></button>
              <button data-testid={`delete-product-${p.code}`} className="p-2 rounded-md hover:bg-rose-50 text-rose-500" onClick={() => del(p.id)}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white border border-slate-200 border-t-0 rounded-b-lg">
        <span className="text-xs text-slate-500" data-testid="page-info">Sayfa {page + 1} / {totalPages}</span>
        <div className="flex gap-2">
          <Button data-testid="prev-page" variant="outline" size="sm" className="h-9 gap-1" disabled={page <= 0 || loading} onClick={() => load(page - 1, q)}>
            <ChevronLeft className="h-4 w-4" /> Önceki
          </Button>
          <Button data-testid="next-page" variant="outline" size="sm" className="h-9 gap-1" disabled={page >= totalPages - 1 || loading} onClick={() => load(page + 1, q)}>
            Sonraki <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Ürünü Düzenle" : "Yeni Ürün"}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">Kod ve açıklama zorunlu, liste fiyatı opsiyoneldir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Kod</Label>
              <Input data-testid="product-form-code" className="mt-1.5 h-11" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Açıklama</Label>
              <Input data-testid="product-form-name" className="mt-1.5 h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Birim</Label>
                <Input data-testid="product-form-unit" className="mt-1.5 h-11" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Liste Fiyat (opsiyonel)</Label>
                <Input
                  data-testid="product-form-price"
                  type="number"
                  inputMode="decimal"
                  step="any"
                  min="0"
                  placeholder="—"
                  className="mt-1.5 h-11"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button data-testid="product-form-save" className="bg-slate-900 hover:bg-slate-800" onClick={save}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
